// From http://www.redblobgames.com/
// Copyright 2015 Red Blob Games <redblobgames@gmail.com>
// License: Apache v2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>

/* A set of colormaps for use with the rendering functions.
 *
 * A colormap is a function that takes (x,y,z) and returns [r,g,b].
 * Annoyingly, x,y are integers in map coordinates and z is 0.0-1.0.
 */

function lookup(array) {
    return function(_x, _y, z) {
        let index = (255 * z) | 0;
        if (index < 0) { index = 0; }
        if (index > 255) { index = 255; }
        return array[index];
    };
}

export function gray() {
    let c = [];
    for (let i = 0; i < 256; i++) {
        c.push([i, i, i]);
    }
    return lookup(c);
}


export function greengray() {
    let c = [];
    for (let i = 0; i < 256; i++) {
        c.push([i*0.6 + 0.4*255,
                i*0.5 + 0.5*255,
                i*0.6 + 0.4*255]);
    }
    return lookup(c);
}


export function grayblue() {
    let c = [];
    for (let i = 0; i < 256; i++) {
        c.push([i*0.6 + 0.1*255,
                i*0.7 + 0.1*255,
                i*1.0 + 0.1*255]);
    }
    return lookup(c);
}


export function waterlevel(terrain) {
    let level = 0;
    const water = [96, 128, 255];
    function c(x, y, z) {
        if (z <= level/255) {
            let darken = (level - z*255)/2;
            return [Math.max(0, water[0]-darken),
                    Math.max(0, water[1]-darken),
                    Math.max(0, water[2]-darken)];
        } else {
            return terrain(x, y, (z-(level/255)) * (255/(255-level)));
        }
    };

    c.setLevel = function(newLevel) { level = newLevel; };
    return c;
}


export function terrainbands() {
    const deepwater = [64, 96, 192];
    const shallowwater = [96, 128, 255];
    const beach = [209, 180, 144];
    const forest = [116, 169, 99];
    const jungle = [65, 126, 98];
    const savannah = [164, 189, 125];
    const desert = [190, 210, 175];
    const snow = [210, 210, 215];
    
    return function(_x, _y, z) {
        if (z < 0.003) return deepwater;
        if (z < 0.004) return shallowwater;
        if (z < 0.004) return beach;
        if (z < 0.100) return forest;
        if (z < 0.300) return jungle;
        if (z < 0.600) return savannah;
        if (z < 0.900) return desert;
        return snow;
    };
}


export function discretebiomes(elevation, moisture) {
    // From mapgen2
    const biome = {
      // Features
      OCEAN: 0x44447a,
      COAST: 0x33335a,
      LAKESHORE: 0x225588,
      LAKE: 0x336699,
      RIVER: 0x225588,
      MARSH: 0x2f6666,
      ICE: 0x99ffff,
      BEACH: 0xa09077,
      ROAD1: 0x442211,
      ROAD2: 0x553322,
      ROAD3: 0x664433,
      BRIDGE: 0x686860,
      LAVA: 0xcc3333,

      // Terrain
      SNOW: 0xdddde4,
      TUNDRA: 0xbbbbaa,
      BARE: 0x888888,
      SCORCHED: 0x555555,
      TAIGA: 0x99aa77,
      SHRUBLAND: 0x889977,
      TEMPERATE_DESERT: 0xc9d29b,
      TEMPERATE_RAIN_FOREST: 0x448855,
      TEMPERATE_DECIDUOUS_FOREST: 0x679459,
      GRASSLAND: 0x88aa55,
      SUBTROPICAL_DESERT: 0xd2b98b,
      TROPICAL_RAIN_FOREST: 0x337755,
      TROPICAL_SEASONAL_FOREST: 0x559944
    };

    function biomeColor(z, m) {
        if (z < 0.1) return biome.OCEAN;
        if (z < 0.12) return biome.BEACH;
        
        if (z > 0.8) {
            if (m < 0.1) return biome.SCORCHED;
            if (m < 0.2) return biome.BARE;
            if (m < 0.5) return biome.TUNDRA;
            return biome.SNOW;
        }

        if (z > 0.6) {
            if (m < 0.33) return biome.TEMPERATE_DESERT;
            if (m < 0.66) return biome.SHRUBLAND;
            return biome.TAIGA;
        }

        if (z > 0.3) {
            if (m < 0.16) return biome.TEMPERATE_DESERT;
            if (m < 0.50) return biome.GRASSLAND;
            if (m < 0.83) return biome.TEMPERATE_DECIDUOUS_FOREST;
            return biome.TEMPERATE_RAIN_FOREST;
        }

        if (m < 0.16) return biome.SUBTROPICAL_DESERT;
        if (m < 0.33) return biome.GRASSLAND;
        if (m < 0.66) return biome.TROPICAL_SEASONAL_FOREST;
        return biome.TROPICAL_RAIN_FOREST;
    }
    
    return function(x, y, _z) {
        // Use the original elevation, not the one modified by an
        // exponent, because the z ranges I defined in mapgen2 are for
        // the unmodified z. Also, I need to reshape these ranges a
        // bit because in mapgen2 I redistribute elevations and
        // moistures, and here I am directly getting it from the
        // perlin noise, which isn't evenly distributed.
        let z = elevation.get(x, y);
        let m = moisture.get(x, y);
        // Reshape these because perlin noise isn't evenly distributed
        z = z * 1.4 - 0.35;
        m = m * 3.0 - 1.0;
        
        let color = biomeColor(z, m);
        return [color >> 16, (color >> 8) & 255, color & 255];
    };
}

export function smoothbiomes(elevation, moisture) {
    let water = 0.1;
    return function(x, y, _) {
        let z = elevation.get(x, y);
        let m = moisture.get(x, y);
        // Reshape these because perlin noise isn't evenly distributed
        z = z * 1.3 - 0.35;
        m = m * 3.0 - 0.7;

        if (z < water) {
            return [48 + 48*z/water, 64 + 64*z/water, 127 + 128*z/water];
        }

        // Green or brown at low elevation, and make it more white-ish
        // as you go up
        z = z * z * 0.7;
        let r = 210 - 100*m, g = 185 - 45*m, b = 139 - 45*m;
        return [255 * z + r * (1-z),
                255 * z + g * (1-z),
                255 * z + b * (1-z)];
    };
}
